/**
 * Thin provider dispatcher — simplified from yaqatw's src/service/cloud/sync.ts.
 * Picks whichever provider the "backend" settings page's `cloudServer` value
 * selects and exposes the raw push/pull/metadata/delete operations.
 *
 * Unlike yaqatw's Sync class, this does NOT do entity-level
 * encryptEntry/decryptEntry (qda's highlight/document model has no
 * `Entry.attrs` shape to field-encrypt) and does NOT own the sync loop
 * itself — src/cloud/qdaSync.ts does that, calling pushRaw/pullRaw/
 * getMetadata directly and handling whole-payload encryption itself via
 * the project's own key (see src/data/crypto/cipher.ts).
 */
import Events from "./events";
import type { FileMetadata, PushResult, UploadResult, SyncEntry } from "./types";
import { getSettingValue } from "../settings/settingsStore";
import Dropbox from "./dropbox";
import GoogleDrive from "./googledrive";
import OneDrive from "./onedrive";
import Nextcloud from "./nextcloud";
import S3Provider from "./s3";
import { log } from "./log";

// Provider interface — kept structurally in lockstep with BaseCloudProvider's
// signatures (which every concrete provider extends).
export interface Provider {
  pull(fileName: string, fileId?: string): Promise<unknown>;
  push(fileName: string, data: unknown): Promise<PushResult | void>;
  pushDiff?(remoteEntries: SyncEntry[]): Promise<void>;
  pullById?(fileId: string): Promise<unknown>;
  delete(filePath: string): Promise<void>;
  getMetadata(path: string): Promise<FileMetadata[]>;
  getMetadataAll(path: string): Promise<FileMetadata[]>;
  uploadFile?(filePath: string, fileContent: Blob | string | ArrayBuffer): Promise<UploadResult>;
  downloadFile?(filePath: string): Promise<Blob>;
  queue: Map<string, Promise<unknown>>;
  tokenName: string;
  resetAuthGate?(): void;
  /** Best-effort, non-interactive verified identity (see BaseCloudProvider). */
  getIdentity?(): Promise<string | null>;
}

export class Sync {
  private provider: Provider | null = null;
  private providerReady: Promise<void>;

  constructor() {
    // Ensure provider is initialized before any methods are called
    this.providerReady = this.initProvider();
    // Re-init when the user picks/changes the backend in Settings, so callers
    // don't have to remember to re-run initProvider() themselves.
    Events.on("settingsUpdated", () => {
      this.providerReady = this.initProvider();
    });
  }

  async initProvider(): Promise<void> {
    try {
      const backend = getSettingValue("backend", "cloudServer");

      switch (backend) {
        case "googleDrive":
          log("Using Google Drive as backend");
          this.provider = GoogleDrive;
          break;
        case "dropBox":
          log("Using Dropbox as backend");
          this.provider = Dropbox;
          break;
        case "oneDrive":
          log("Using OneDrive as backend");
          this.provider = OneDrive;
          break;
        case "nextCloud":
          log("Using Nextcloud OAuth as backend");
          await Nextcloud.initOAuth();
          this.provider = Nextcloud;
          break;
        case "s3":
          log("Using S3-compatible storage as backend");
          this.provider = S3Provider;
          break;
        default:
          log("Backend not set");
          this.provider = null;
      }
    } catch (error: any) {
      Events.emit("showStatus", {
        text: "Error initializing provider: " + error.message,
        intent: "error",
      });
      this.provider = null;
    }
  }

  async ensureProvider(): Promise<Provider> {
    await this.providerReady;
    // The singleton is constructed during a circular-import window
    // (settingsStore → registry → pages/backend → sync), so the very first
    // initProvider() can run before useSettingsStore exists — leaving
    // provider null even though a backend IS configured. Nothing re-inits
    // until a `settingsUpdated` event, so on a fresh load with a saved
    // backend this would replay a stale null forever. Retry once now that
    // any real call-time guarantees settings are loaded; if the backend is
    // genuinely disabled this just re-sets null and we throw below.
    if (!this.provider) {
      this.providerReady = this.initProvider();
      await this.providerReady;
    }
    if (!this.provider) {
      throw new Error(
        "Provider not initialized. If you want to sync with cloud storage (recommended), please set a valid backend in Settings > Backend."
      );
    }
    return this.provider;
  }

  /**
   * Best-effort verified identity from the active provider, used to rate-limit
   * collaboration signaling per user. Non-interactive: returns null (never
   * prompts) when no provider is configured, the provider has no identity
   * concept (S3), or the user isn't already authenticated. Collab falls back to
   * a stable device id when this is null — see src/collab/identity.ts.
   */
  async getIdentity(): Promise<string | null> {
    try {
      await this.providerReady;
      if (this.provider?.getIdentity) return await this.provider.getIdentity();
    } catch {
      /* ignore — fall through to null so collaboration uses the device id */
    }
    return null;
  }

  /* ── Cloud operations ── */

  async deleteFromCloud(key: string): Promise<void> {
    const provider = await this.ensureProvider();
    try {
      Events.emit("showStatus", { intent: "info", text: `Deleting ${key} from cloud...` });
      await provider.delete(key);
      Events.emit("showStatus", {
        intent: "success",
        text: `${key} deleted from cloud successfully`,
      });
    } catch (error) {
      console.error(`Error deleting ${key} from cloud:`, error);
      Events.emit("showStatus", {
        intent: "error",
        text: `Failed to delete ${key}: ${(error as any)?.message || String(error)}`,
      });
      throw error;
    }
  }

  async pushRaw(key: string, data: any): Promise<{ modifiedTime: string } | void> {
    const provider = await this.ensureProvider();
    return provider.push(key, data);
  }

  async pullRaw(key: string, fileId?: string): Promise<any> {
    const provider = await this.ensureProvider();
    return provider.pull(key.toLowerCase(), fileId);
  }

  async getMetadata(path: string): Promise<any[]> {
    const provider = await this.ensureProvider();
    return provider.getMetadata(path.toLocaleLowerCase());
  }

  async getMetadataAll(projectName: string): Promise<any[]> {
    const provider = await this.ensureProvider();
    return provider.getMetadataAll(projectName);
  }

  async pullById(fileId: string): Promise<any> {
    const provider = await this.ensureProvider();
    if (!provider.pullById) throw new Error("Current provider does not support pull-by-id");
    return provider.pullById(fileId);
  }

  async clearToken(backend: string): Promise<void> {
    try {
      const provider = await this.ensureProvider();
      localStorage.removeItem(provider.tokenName);

      if (backend === "nextCloud") {
        localStorage.removeItem(Nextcloud.refreshTokenName);
      }

      // S3 credentials live in settings (not localStorage), so clear them there.
      if (backend === "s3") {
        await S3Provider.disconnect();
      }

      Events.emit("showStatus", {
        intent: "info",
        text: `${backend} credentials cleared. You will be prompted to login again when needed.`,
      });
      log(`Token cleared for backend: ${backend}. Will re-authenticate on next sync.`);
    } catch (error) {
      console.error(`Error clearing token for ${backend}:`, error);
      Events.emit("showStatus", {
        intent: "error",
        text: `Failed to clear credentials: ${(error as any).message}`,
      });
      throw error;
    }
  }

  async uploadFile(filePath: string, fileContent: Blob | string | ArrayBuffer): Promise<any> {
    const provider = await this.ensureProvider();
    if (!provider.uploadFile) {
      throw new Error("Current provider does not support file uploads");
    }
    return provider.uploadFile(filePath, fileContent);
  }

  async downloadFile(filePath: string): Promise<Blob> {
    const provider = await this.ensureProvider();
    if (!provider.downloadFile) {
      throw new Error("Current provider does not support raw file downloads");
    }
    return provider.downloadFile(filePath);
  }
}

// Singleton instance
export const sync = new Sync();
export default sync;
